import { create } from 'zustand';

/**
 * Detecta um deploy novo do front comparando o build em execução com o
 * /version.json publicado (gerado no build, servido sem cache).
 *
 * Ao detectar: abre o UpdateAvailableModal com o botão "Atualizar" e recarrega
 * sozinho na próxima troca de rota. Se o usuário ficar na mesma tela, a atualização é
 * forçada após FORCE_RELOAD_AFTER_MS — o prazo dá tempo de salvar o que
 * estiver em andamento.
 */
const CHECK_INTERVAL_MS = 5 * 60 * 1000;
const FORCE_RELOAD_AFTER_MS = 10 * 60 * 1000;

/** Estado lido pelo UpdateAvailableModal. */
export const useAppUpdateStore = create<{ updateAvailable: boolean }>(() => ({
  updateAvailable: false,
}));

let updateAvailable = false;
let checking = false;

async function fetchPublishedBuildId(): Promise<string | null> {
  try {
    const response = await fetch(`/version.json?t=${Date.now()}`, {
      cache: 'no-store',
    });
    if (!response.ok) return null;
    const data = (await response.json()) as { buildId?: unknown };
    return typeof data.buildId === 'string' ? data.buildId : null;
  } catch {
    // offline, deploy em andamento ou resposta não-JSON: tenta na próxima
    return null;
  }
}

async function checkForUpdate(): Promise<void> {
  if (updateAvailable || checking) return;
  checking = true;
  try {
    const published = await fetchPublishedBuildId();
    if (!published || published === __APP_BUILD_ID__) return;

    updateAvailable = true;
    useAppUpdateStore.setState({ updateAvailable: true });
    setTimeout(() => window.location.reload(), FORCE_RELOAD_AFTER_MS);
  } finally {
    checking = false;
  }
}

interface NavigationSource {
  subscribe: (eventType: 'onResolved', fn: (event: { pathChanged: boolean }) => void) => () => void;
}

export function startVersionCheck(router: NavigationSource): void {
  if (!import.meta.env.PROD) return;

  setInterval(checkForUpdate, CHECK_INTERVAL_MS);
  document.addEventListener('visibilitychange', () => {
    if (document.visibilityState === 'visible') void checkForUpdate();
  });

  router.subscribe('onResolved', ({ pathChanged }) => {
    if (!pathChanged) return;
    if (updateAvailable) {
      window.location.reload();
      return;
    }
    void checkForUpdate();
  });
}
